/**
 * The app shell, Grimoire's layout: a fixed sidebar (wordmark, Browse, Contribute, account at
 * the foot) and a main column whose top bar holds search. Below 900px the sidebar becomes a
 * drawer behind a menu button.
 */
import { useEffect, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router'
import {
  BookOpen,
  Building2,
  ClipboardCheck,
  Flag,
  History,
  Layers,
  Library,
  LogIn,
  Menu,
  Plus,
  Shield,
  UserRound,
  Users,
  X,
} from 'lucide-react'
import { SearchBox } from './SearchBox'
import { GameSwitch } from './GameSwitch'
import { Avatar } from './Bits'
import { Flash } from './Flash'
import { signInHref, useSession } from '../lib/session'
import { useQuery } from '../lib/query'
import { num } from '../lib/format'
import type { Stats } from '../lib/types'

function NavItem({
  to,
  icon: Icon,
  children,
  count,
  tone = 'pending',
}: {
  to: string
  icon: typeof Library
  children: React.ReactNode
  count?: number
  tone?: 'pending' | 'flag'
}) {
  return (
    <NavLink to={to} className="nav-item" end={to === '/'}>
      <Icon size={18} aria-hidden="true" />
      <span>{children}</span>
      {count ? (
        <span className={tone === 'flag' ? 'nav-count nav-count-flag' : 'nav-count'} aria-label={`${count} waiting`}>
          {num(count)}
        </span>
      ) : null}
    </NavLink>
  )
}

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const session = useSession()
  const location = useLocation()
  const { data: stats } = useQuery<Stats>('/api/v1/stats', { staleMs: 60_000 })
  return (
    <nav
      className="sidebar-inner"
      aria-label="Main"
      onClick={(e) => (e.target as HTMLElement).closest('a') && onNavigate?.()}
    >
      <Link to="/" className="wordmark" aria-label="GrimoireCodexDB, home">
        <img src="/codexdb-logo.svg" alt="" width="36" height="24" />
        <span translate="no" className="wordmark-text">
          <span className="wordmark-name">Grimoire</span>
          <span className="wordmark-sub">CodexDB</span>
        </span>
      </Link>

      {session.wargames && <GameSwitch />}

      <div className="nav-group">
        <h2 className="nav-heading">Browse</h2>
        <NavItem to="/systems" icon={Library}>
          Systems
        </NavItem>
        <NavItem to="/books" icon={BookOpen}>
          Books
        </NavItem>
        <NavItem to="/series" icon={Layers}>
          Series & settings
        </NavItem>
        <NavItem to="/publishers" icon={Building2}>
          Publishers
        </NavItem>
        <NavItem to="/creators" icon={Users}>
          Creators
        </NavItem>
      </div>

      <div className="nav-group">
        <h2 className="nav-heading">Contribute</h2>
        <NavItem to="/review" icon={ClipboardCheck} count={stats?.pending}>
          Review queue
        </NavItem>
        <NavItem to="/changes" icon={History}>
          Recent changes
        </NavItem>
        <NavItem to="/new" icon={Plus}>
          Add a record
        </NavItem>
      </div>

      {session.can('moderator') && (
        <div className="nav-group">
          <h2 className="nav-heading">Moderate</h2>
          <NavItem
            to="/moderation"
            icon={Flag}
            tone="flag"
            count={
              (stats?.open_reports ?? 0) +
              (session.can('admin') ? (stats?.pending_claims ?? 0) + (stats?.open_support ?? 0) : 0)
            }
          >
            Moderation
          </NavItem>
          {session.can('admin') && (
            <NavItem to="/admin" icon={Shield}>
              People and roles
            </NavItem>
          )}
        </div>
      )}

      <div className="sidebar-foot">
        <nav className="foot-links" aria-label="About">
          <Link to="/about">About</Link>
          <Link to="/about#review">Guidelines</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/support">Support</Link>
          <Link to="/developers">API</Link>
        </nav>
        {session.user ? (
          <NavLink to="/account" className="account-chip">
            <Avatar name={session.user.name} src={session.user.avatar_url} size={28} />
            <span className="account-name">{session.user.name}</span>
            <span className="account-role">{session.user.role}</span>
          </NavLink>
        ) : session.loading ? (
          // Held until the session is known: no "Sign in" flash for people who are signed in, and
          // none in the prerendered pages, which crawlers would read as a login wall.
          <div className="sidebar-signin-slot" aria-hidden="true" />
        ) : (
          <Link to={signInHref(location.pathname + location.search)} className="button button-quiet sidebar-signin">
            <LogIn size={16} aria-hidden="true" /> Sign in to contribute
          </Link>
        )}
      </div>
    </nav>
  )
}

export function Shell() {
  const [drawer, setDrawer] = useState(false)
  const location = useLocation()
  const session = useSession()

  useEffect(() => setDrawer(false), [location.pathname])
  useEffect(() => {
    if (!drawer) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setDrawer(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawer])

  return (
    <div className="shell" data-drawer={drawer || undefined}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <aside className="sidebar" aria-label="Site navigation">
        <Sidebar onNavigate={() => setDrawer(false)} />
      </aside>
      <div className="drawer-scrim" onClick={() => setDrawer(false)} aria-hidden="true" />
      <div className="main-col">
        <header className="topbar">
          <button
            type="button"
            className="icon-button menu-button"
            aria-label={drawer ? 'Close menu' : 'Open menu'}
            aria-expanded={drawer}
            onClick={() => setDrawer((d) => !d)}
          >
            {drawer ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link to="/" className="topbar-mark" aria-label="GrimoireCodexDB, home">
            <img src="/codexdb-logo.svg" alt="" width="34" height="22" />
          </Link>
          <SearchBox />
          {!session.user && !session.loading && (
            <Link to={signInHref(location.pathname + location.search)} className="button button-quiet topbar-signin">
              <UserRound size={16} aria-hidden="true" />
              <span>Sign in</span>
            </Link>
          )}
        </header>
        <main id="main" className="main" tabIndex={-1}>
          <Flash />
          <Outlet />
        </main>
      </div>
    </div>
  )
}
